import type { RefObject } from "react";
import { useContext, useEffect } from "react";

import isNil from "lodash/isNil";

import { UserActivityContext } from "@/context-providers/UserActivityProvider";

interface UseUserInactivityTimeoutArgs {
  onInactivityTimeoutRef?: RefObject<() => Promise<void>>;
}

/**
 * Hook to access the UserActivityContext and optionally register a background inactivity timeout handler.
 * Must be used within a UserActivityProvider.
 *
 * @param onInactivityTimeoutRef - Optional callback ref for background inactivity timeout
 * @returns UserActivityContext value with isInactive, markActivity, and registerBackgroundInactivityHandler
 */
const useUserInactivityTimeout = (args?: UseUserInactivityTimeoutArgs) => {
  const context = useContext(UserActivityContext);

  if (!context) {
    throw new Error(
      "useUserInactivityTimeout must be used within a UserActivityProvider",
    );
  }

  // If onInactivityTimeoutRef is provided, register it for background inactivity handling
  const { setBackgroundInactivityHandler } = context;
  const onInactivityTimeoutRef = args?.onInactivityTimeoutRef;
  useEffect(() => {
    if (isNil(onInactivityTimeoutRef)) return;
    setBackgroundInactivityHandler(onInactivityTimeoutRef);
  }, [onInactivityTimeoutRef, setBackgroundInactivityHandler]);

  return context;
};

export default useUserInactivityTimeout;
